import { Avatar, Box, Button, Card, Divider, Grid, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import ModalCreateOrganization from 'Components/ModalCreateOrganization';
import useTranslate from 'Hooks/useTranslate';
import useNotification from 'Hooks/useNotification';
import { useAvatarColor } from 'Hooks/useAvatarColor';
import Skeleton from 'Theme/components/Skeleton';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Link, generatePath, useParams, useLocation } from 'react-router-dom';
import { PrivateRoutes } from 'Router/Routes';
import Helmet from 'react-helmet';
import { GetOrganizationOwnerInvitationStatus } from 'Apollo/queries/organization/GetOrganizationOwnerInvitationStatus';
import { useStyles } from './Organizations.styles';

interface Props {
  loading: boolean;
  dataResult: IOrganization[];
}

function OrganizationCard({
  row,
  accountCode,
  isSelectMode,
  onSelect
}: Readonly<{ row: IOrganization; accountCode: string; isSelectMode: boolean; onSelect?: (org: IOrganization) => void }>) {
  const ts = useTranslate();
  const classes = useStyles();
  const bgColor = useAvatarColor({ identifier: row.code });

  return (
    <Card
      role="organizationCard"
      data-testid={`organization-${row.code}`}
      className={classes.card}
      elevation={1}
    >
      <div className={classes.cardBody}>
        <div className={classes.cardTop}>
          <Typography className={classes.orgName}>{row.name}</Typography>
          <Avatar className={classes.orgAvatar} style={{ backgroundColor: bgColor }}>
            {row.name.charAt(0).toUpperCase()}
          </Avatar>
        </div>
        <Typography className={classes.createdDate}>
          {ts('page.organizations.createdDate', {
            value: new Date(row.dateCreation).toLocaleDateString()
          })}
        </Typography>
      </div>
      <Divider />
      <div className={classes.cardFooter}>
        {isSelectMode ? (
          <Button
            color="primary"
            size="small"
            endIcon={<ArrowForward fontSize="small" />}
            onClick={() => onSelect?.(row)}
            className={classes.seeMoreButton}
          >
            {ts('common.action.select')}
          </Button>
        ) : (
          <Button
            color="primary"
            size="small"
            endIcon={<ArrowForward fontSize="small" />}
            component={Link}
            to={generatePath(PrivateRoutes.organizationProfile.path, {
              accountCode,
              organizationCode: row.code
            })}
            className={classes.seeMoreButton}
          >
            {ts('common.action.see.seeMore')}
          </Button>
        )}
      </div>
    </Card>
  );
}

function Organizations({ loading, dataResult }: Readonly<Props>) {
  const ts = useTranslate();
  const classes = useStyles();
  const { accountCode: accountCodeFromParams } = useParams<{ accountCode: string }>();
  const location = useLocation();
  const { onError } = useNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<IOrganization | null>(null);
  const hasAutoOpened = useRef(false);

  const token = new URLSearchParams(location.search).get('token') ?? '';

  const { data: invitationData } = useQuery<
    GetOrganizationOwnerInvitationStatusData,
    GetOrganizationOwnerInvitationStatusVars
  >(GetOrganizationOwnerInvitationStatus, {
    variables: { token },
    skip: !token,
    onError
  });

  const invitationStatus = invitationData?.organizationOwnerInvitationStatus;
  const isSelectMode = invitationStatus?.isValid === true;
  const requesterName = invitationStatus
    ? `${invitationStatus.notifierFirstName} ${invitationStatus.notifierLastName}`.trim()
    : undefined;
  const accountCode = invitationStatus?.accountCode ?? accountCodeFromParams ?? '';
  const accountLabel = invitationStatus?.accountLabel ?? '';

  useEffect(() => {
    if (isSelectMode && !loading && dataResult?.length === 0 && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      setIsModalOpen(true);
    }
  }, [isSelectMode, loading, dataResult]);

  const handleSelectOrg = (org: IOrganization) => {
    setSelectedOrg(org);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedOrg(null);
  };

  return (
    <Box className={classes.wrapper}>
      <Helmet title={ts('page.organizations.title')} />
      <Typography variant="h5" className={classes.pageTitle}>
        {ts('page.organizations.yourOrganizations')}
      </Typography>
      <Grid container spacing={2}>
        {loading &&
          (['s1', 's2', 's3', 's4'] as const).map(key => (
            <Grid item key={key}>
              <Skeleton variant="rectangular" width={200} height={200} />
            </Grid>
          ))}
        {!loading &&
          dataResult?.length > 0 &&
          dataResult.map(row => (
            <Grid item key={row.code}>
              <OrganizationCard row={row} accountCode={accountCode} isSelectMode={isSelectMode} onSelect={handleSelectOrg} />
            </Grid>
          ))}
        {!loading && isSelectMode && (
          <Grid item>
            <Card className={classes.addOrgCard} onClick={() => { setSelectedOrg(null); setIsModalOpen(true); }}>
              <AddIcon className={classes.addOrgIcon} />
              <Typography className={classes.addOrgLabel}>
                {ts('page.organizations.addOrganization')}
              </Typography>
            </Card>
          </Grid>
        )}
      </Grid>
      {selectedOrg ? (
        <ModalCreateOrganization
          key={selectedOrg.code}
          mode="Select"
          isOpen={isModalOpen}
          onClose={handleModalClose}
          accountCode={accountCode}
          accountLabel={accountLabel}
          organizationId={selectedOrg.code}
          organizationName={selectedOrg.name}
          initialDescription={selectedOrg.description ?? undefined}
          requesterEmail={requesterName}
        />
      ) : (
        <ModalCreateOrganization
          mode="Create"
          isOpen={isModalOpen}
          onClose={handleModalClose}
          accountCode={accountCode}
          accountLabel={accountLabel}
          requesterEmail={requesterName}
        />
      )}
    </Box>
  );
}

export default Organizations;
